// MeetingTable — grouped parent/child/one-off table for the meetings
// tab. Mirrors DeliverableTable's row vocabulary so the two surfaces
// read consistently. Columns: Name 38% / Attendees 16% / Date 22% /
// Status 15% / Actions 9%. Sticky header. Parent rows carry the
// recurrence chevron + OccurrenceStrip; child rows render the
// auto-suffixed occurrence title; one-off rows reserve the chevron
// column with an invisible placeholder so columns line up across
// row variants.
//
// OccurrenceStrip is reused from the deliverable folder — meeting
// derived statuses are mapped to the deliverable-strip status enum at
// the call site so the visual palette stays unified across tabs.

import { Fragment, type ReactNode } from "react";
import type { MeetingGraceConfig } from "./meetingGraceDays";
import {
  Pencil,
  Archive as ArchiveIcon,
  ChevronRight,
} from "lucide-react";
import Button from "../../../common/Button";
import MeetingOccurrenceStrip, {
  type MeetingOccurrenceTile,
} from "./MeetingOccurrenceStrip";
import AttendeeStack from "../shared/AttendeeStack";
import MeetingDateCell from "./MeetingDateCell";
import MeetingStatusPill from "./MeetingStatusPill";
import { deriveMeetingStatus } from "./deriveMeetingStatus";
import { deriveMeetingOccurrenceTitle } from "./deriveMeetingOccurrenceTitle";
import type { Meeting } from "../../../../types";

interface ChildRow {
  kind: "child";
  meeting: Meeting;
  parent: Meeting;
}
interface ParentRow {
  kind: "parent";
  meeting: Meeting;
  children: ChildRow[];
}
interface OneOffRow {
  kind: "one-off";
  meeting: Meeting;
}
export type MeetingRow = ParentRow | ChildRow | OneOffRow;

export type MeetingAction = "edit" | "archive" | "primary";

export interface MeetingTableProps {
  rows: MeetingRow[];
  expandedScheduleIds: Set<string>;
  onToggleExpand: (parentId: string) => void;
  onRowClick: (row: ChildRow | OneOffRow) => void;
  onAction: (row: MeetingRow, action: MeetingAction) => void;
  highlightRowId?: string | null;
  today?: Date;
  /** Per-contract on-time grace override (days). When set, all rows
   *  use this fixed window for the held-meeting early/late caption
   *  rather than the cadence-proportional default. */
  graceOverride?: number | MeetingGraceConfig | null;
  /** Meeting ID currently shown in the inline detail expand. When set,
   *  an extra `<tr>` is injected directly after that meeting's row so
   *  the panel stays attached to the clicked occurrence rather than
   *  floating below a long recurrence block. */
  expandedItemId?: string | null;
  /** Renderer for the inline detail panel. Called once per render when
   *  expandedItemId matches a row in the table; receives the meeting
   *  for that row. The renderer returns the panel content; the table
   *  wraps it in a `<tr><td colspan>` so it spans every column. */
  renderExpandedRow?: (meeting: Meeting) => ReactNode;
}

const COLUMN_COUNT = 5;

const FOCUS_RING =
  "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-inset";

const ARCHIVED_ROW_MUTE = "opacity-60 text-slate-400 dark:text-slate-500";

// Darker neutral fill for the row whose inline detail panel is open, so it
// reads as the obvious source of the popout. Matches the shared TrackingTable
// + DeliverableTable selected-row treatment for cross-type consistency.
const SELECTED_ROW_CLASS = "bg-slate-100 dark:bg-slate-700";

const TH_BASE =
  "px-3 py-2 font-medium bg-slate-50 dark:bg-slate-800";

function frequencyLabel(freq: string): string {
  switch (freq) {
    case "daily":
      return "Daily";
    case "weekly":
      return "Weekly";
    case "monthly":
      return "Monthly";
    case "quarterly":
      return "Quarterly";
    case "annually":
      return "Annually";
    default:
      return freq;
  }
}

function formatMonthYearIso(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function parentSubline(parent: Meeting): string {
  const rule = parent.recurrenceRule;
  if (!rule) return "";
  const parts = [
    frequencyLabel(rule.frequency),
    formatMonthYearIso(rule.startDate),
  ];
  if (rule.endDate) parts.push(`– ${formatMonthYearIso(rule.endDate)}`);
  return parts.filter(Boolean).join(" · ");
}

function toOccurrenceTile(
  c: ChildRow,
  today: Date,
): MeetingOccurrenceTile {
  return {
    id: c.meeting.id,
    status: deriveMeetingStatus(c.meeting.status, c.meeting.date, today),
    period: deriveMeetingOccurrenceTitle(c.meeting, c.parent),
  };
}

interface ActionButtonsProps {
  row: MeetingRow;
  meeting: Meeting;
  onAction: (row: MeetingRow, action: MeetingAction) => void;
}

function ActionButtons({ row, meeting, onAction }: ActionButtonsProps) {
  return (
    <div className="inline-flex gap-1">
      <Button
        variant="ghost"
        iconOnly
        size="sm"
        icon={<Pencil className="h-4 w-4" />}
        aria-label={`Edit ${meeting.name}`}
        title={`Edit ${meeting.name}`}
        data-testid={`action-edit-${meeting.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onAction(row, "edit");
        }}
      />
      <Button
        variant="ghost"
        iconOnly
        size="sm"
        icon={<ArchiveIcon className="h-4 w-4" />}
        aria-label={`Archive ${meeting.name}`}
        title={`Archive ${meeting.name}`}
        data-testid={`action-archive-${meeting.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onAction(row, "archive");
        }}
      />
    </div>
  );
}

interface ParentRowRenderProps {
  row: ParentRow;
  expanded: boolean;
  highlighted: boolean;
  today: Date;
  onToggleExpand: (parentId: string) => void;
  onAction: (row: MeetingRow, action: MeetingAction) => void;
}

function ParentRowRender({
  row,
  expanded,
  highlighted,
  today,
  onToggleExpand,
  onAction,
}: ParentRowRenderProps) {
  const parent = row.meeting;
  const borderClass = expanded
    ? "border-l-2 border-brand-600 dark:border-brand-400"
    : "border-l-2 border-transparent";
  return (
    <tr
      data-testid={`meeting-row-parent-${parent.id}`}
      tabIndex={0}
      role="row"
      aria-expanded={expanded}
      onClick={() => onToggleExpand(parent.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggleExpand(parent.id);
        }
      }}
      className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${borderClass} ${FOCUS_RING} ${
        highlighted ? "ring-2 ring-brand-400" : ""
      }`}
    >
      <td className="px-3 py-2">
        <div className="flex items-start gap-2">
          <span
            data-testid={`parent-chevron-${parent.id}`}
            className={`mt-0.5 inline-flex shrink-0 transition-transform ${
              expanded ? "rotate-90" : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(parent.id);
            }}
          >
            <ChevronRight className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          </span>
          <div className="flex flex-col">
            <span className="inline-flex items-center gap-2">
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {parent.name}
              </span>
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-caption font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                Recurring
              </span>
            </span>
            <span
              data-testid={`parent-subline-${parent.id}`}
              className="text-caption text-slate-500 dark:text-slate-400"
            >
              {parentSubline(parent)}
            </span>
            <div className="mt-1">
              <MeetingOccurrenceStrip
                occurrences={row.children.map((c) =>
                  toOccurrenceTile(c, today),
                )}
              />
            </div>
          </div>
        </div>
      </td>
      <td
        data-testid={`parent-attendees-cell-${parent.id}`}
        className="px-3 py-2 align-top text-slate-400 dark:text-slate-500"
      >
        —
      </td>
      <td
        data-testid={`parent-date-cell-${parent.id}`}
        className="px-3 py-2 align-top text-slate-400 dark:text-slate-500"
      >
        —
      </td>
      <td
        data-testid={`parent-status-cell-${parent.id}`}
        className="px-3 py-2 align-top text-slate-400 dark:text-slate-500"
      >
        —
      </td>
      <td className="px-3 py-2 align-top text-right">
        <ActionButtons row={row} meeting={parent} onAction={onAction} />
      </td>
    </tr>
  );
}

interface ChildRowRenderProps {
  row: ChildRow;
  highlighted: boolean;
  today: Date;
  graceOverride?: number | MeetingGraceConfig | null;
  onRowClick: (row: ChildRow | OneOffRow) => void;
  onAction: (row: MeetingRow, action: MeetingAction) => void;
  /** True when this row's inline detail panel is open. */
  selected?: boolean;
}

function ChildRowRender({
  row,
  highlighted,
  today,
  graceOverride,
  onRowClick,
  onAction,
  selected = false,
}: ChildRowRenderProps) {
  const m = row.meeting;
  const title = deriveMeetingOccurrenceTitle(m, row.parent);
  const derived = deriveMeetingStatus(m.status, m.date, today);
  return (
    <tr
      id={`meeting-row-child-${m.id}`}
      data-testid={`meeting-row-child-${m.id}`}
      tabIndex={0}
      role="row"
      onClick={() => onRowClick(row)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick(row);
        }
      }}
      className={`cursor-pointer border-l-2 border-brand-400/25 dark:border-brand-400/30 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${FOCUS_RING} ${
        highlighted ? "ring-2 ring-brand-400" : ""
      } ${selected ? SELECTED_ROW_CLASS : ""} ${m.archivedAt ? ARCHIVED_ROW_MUTE : ""}`}
    >
      <td className="pl-8 pr-3 py-2">
        <span className="inline-flex items-center gap-2">
          <span className="text-slate-900 dark:text-slate-100">{title}</span>{" "}
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-caption font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            Occurrence
          </span>
        </span>
      </td>
      <td className="px-3 py-2">
        <AttendeeStack attendees={m.attendees ?? []} />
      </td>
      <td className="px-3 py-2">
        <MeetingDateCell
          status={m.status}
          date={m.date}
          heldDate={m.heldDate}
          recurrenceRule={row.parent.recurrenceRule}
          today={today}
          graceOverride={graceOverride}
        />
      </td>
      <td className="px-3 py-2">
        <MeetingStatusPill status={derived} />
      </td>
      <td className="px-3 py-2 text-right">
        <ActionButtons row={row} meeting={m} onAction={onAction} />
      </td>
    </tr>
  );
}

interface OneOffRowRenderProps {
  row: OneOffRow;
  highlighted: boolean;
  today: Date;
  graceOverride?: number | MeetingGraceConfig | null;
  onRowClick: (row: ChildRow | OneOffRow) => void;
  onAction: (row: MeetingRow, action: MeetingAction) => void;
  /** True when this row's inline detail panel is open. */
  selected?: boolean;
}

function OneOffRowRender({
  row,
  highlighted,
  today,
  graceOverride,
  onRowClick,
  onAction,
  selected = false,
}: OneOffRowRenderProps) {
  const m = row.meeting;
  const derived = deriveMeetingStatus(m.status, m.date, today);
  return (
    <tr
      data-testid={`meeting-row-oneoff-${m.id}`}
      tabIndex={0}
      role="row"
      onClick={() => onRowClick(row)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick(row);
        }
      }}
      className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${FOCUS_RING} ${
        highlighted ? "ring-2 ring-brand-400" : ""
      } ${selected ? SELECTED_ROW_CLASS : ""} ${m.archivedAt ? ARCHIVED_ROW_MUTE : ""}`}
    >
      <td className="px-3 py-2">
        <div className="flex items-start gap-2">
          <span
            data-testid={`chevron-placeholder-${m.id}`}
            className="invisible mt-0.5 inline-flex shrink-0"
            aria-hidden="true"
          >
            <ChevronRight className="h-4 w-4" />
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="text-slate-900 dark:text-slate-100">
              {m.name}
            </span>
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-caption font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              One-off
            </span>
          </span>
        </div>
      </td>
      <td className="px-3 py-2">
        <AttendeeStack attendees={m.attendees ?? []} />
      </td>
      <td className="px-3 py-2">
        <MeetingDateCell
          status={m.status}
          date={m.date}
          heldDate={m.heldDate}
          today={today}
          graceOverride={graceOverride}
        />
      </td>
      <td className="px-3 py-2">
        <MeetingStatusPill status={derived} />
      </td>
      <td className="px-3 py-2 text-right">
        <ActionButtons row={row} meeting={m} onAction={onAction} />
      </td>
    </tr>
  );
}

export default function MeetingTable({
  rows,
  expandedScheduleIds,
  onToggleExpand,
  onRowClick,
  onAction,
  highlightRowId,
  today = new Date(),
  graceOverride,
  expandedItemId,
  renderExpandedRow,
}: MeetingTableProps) {
  const inlineExpand = (meeting: Meeting): ReactNode => {
    if (!renderExpandedRow || expandedItemId !== meeting.id) return null;
    return (
      <tr data-testid={`meeting-inline-expand-${meeting.id}`}>
        {/* Quiet neutral band + thin brand connector, matching the shared
         *  TrackingTable expanded cell. The white detail card comes from
         *  TrackingDetailPanel itself, so render the detail directly here —
         *  no extra wrapper card (that produced a double border). */}
        <td
          colSpan={COLUMN_COUNT}
          className="bg-slate-50 dark:bg-slate-800/40 border-l-2 border-brand-500 rounded-r-md p-3"
        >
          {renderExpandedRow(meeting)}
        </td>
      </tr>
    );
  };
  if (rows.length === 0) {
    return (
      <div
        data-testid="meeting-table-empty-state"
        className="rounded-md border border-slate-200 bg-white p-8 text-center text-body text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400"
      >
        No meetings recorded yet.
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded-lg border border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
      <table
        data-testid="meeting-table"
        className="min-w-full border-collapse text-body-sm"
      >
        <thead className="sticky top-0 z-[5] bg-slate-50 dark:bg-slate-800">
          <tr className="text-left text-caption uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <th className={`${TH_BASE} w-[38%]`}>Name</th>
            <th className={`${TH_BASE} w-[16%]`}>Attendees</th>
            <th className={`${TH_BASE} w-[22%]`}>Date</th>
            <th className={`${TH_BASE} w-[15%]`}>Status</th>
            <th className={`${TH_BASE} w-[9%] text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody className="[&>tr:not(:first-child)]:border-t [&>tr:not(:first-child)]:border-t-slate-200 dark:[&>tr:not(:first-child)]:border-t-slate-700">
          {rows.map((row) => {
            if (row.kind === "parent") {
              const expanded = expandedScheduleIds.has(row.meeting.id);
              return (
                <Fragment key={`parent-${row.meeting.id}`}>
                  <ParentRowRender
                    row={row}
                    expanded={expanded}
                    highlighted={highlightRowId === row.meeting.id}
                    today={today}
                    onToggleExpand={onToggleExpand}
                    onAction={onAction}
                  />
                  {expanded &&
                    row.children.map((c) => (
                      <Fragment key={`child-${c.meeting.id}`}>
                        <ChildRowRender
                          row={c}
                          highlighted={highlightRowId === c.meeting.id}
                          today={today}
                          graceOverride={graceOverride}
                          onRowClick={onRowClick}
                          onAction={onAction}
                          selected={expandedItemId === c.meeting.id}
                        />
                        {inlineExpand(c.meeting)}
                      </Fragment>
                    ))}
                </Fragment>
              );
            }
            if (row.kind === "one-off") {
              return (
                <Fragment key={`oneoff-${row.meeting.id}`}>
                  <OneOffRowRender
                    row={row}
                    highlighted={highlightRowId === row.meeting.id}
                    today={today}
                    graceOverride={graceOverride}
                    onRowClick={onRowClick}
                    onAction={onAction}
                    selected={expandedItemId === row.meeting.id}
                  />
                  {inlineExpand(row.meeting)}
                </Fragment>
              );
            }
            return null;
          })}
        </tbody>
      </table>
    </div>
  );
}
